import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import Button from 'components/field/Button';
import CopyComponent from 'components/field/CopyComponent';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { USERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

/**
 * @author @omerrkosar
 */

const ResetPasswordForm = NiceModal.create(({ id, resetLink, email }) => {
	const modal = useModal();
	const [isLoading, setIsLoading] = useState(false);

	const axiosPrivate = useAxiosPrivate();
	const { showError } = useAlert();

	const handleCloseModal = useCallback(() => {
		modal.remove();
	}, []);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					email: yup.string().email().nullable(),
					resetLink: yup.string().nullable()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			email,
			resetLink
		}
	});

	const { watch, reset } = formMethods;

	// If mode is edit or view we gotta get the user data .
	const fillForm = useCallback(() => {
		setIsLoading(true);
		axiosPrivate
			.post(USERS.REQUEST_PASSWORD_RESET, { ID: id })
			.then((response) => {
				const { data, status } = response;

				if (status === 200) {
					reset({
						resetLink: data.link ?? resetLink,
						email: data.email ?? email
					});
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [id]);

	useEffect(() => {
		if (id && !resetLink) fillForm();
	}, [id]);

	const defaultColSpan = {
		md: 12
	};

	return (
		<Modal fullwidth maxWidth="small" className="modal-reset-password">
			<ModalTitle>
				<Typography variant="p1">copyResetPasswordLink</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="email" isLoading={isLoading}>
						<CopyComponent value={watch('email')} />
					</FormItem>
					<FormItem
						label="resetLink"
						colSpan={defaultColSpan}
						toolTip="tooltip.resetlink"
						isLoading={isLoading}>
						<CopyComponent value={watch('resetLink')} share toEmail={watch('email')} />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="close" variant="outline" onClick={handleCloseModal} />
			</ModalActions>
		</Modal>
	);
});

export default ResetPasswordForm;
